import { brand } from "../../config/theme";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";

import { useUserStore } from "../../store/userStore";
import { useSessionCheck } from "../../hooks/useSessionCheck";
import { setLoggingOut } from "../../utils/tokenUtils";
import { logoutUser } from "../../pages/login/functions";

import { Header } from "../../components/base/Header";
import { AppsSidebar, useFilteredCategories } from "../../components/base/AppsSidebar";
import { AppSidebarMenu } from "../../components/base/AppSidebarMenu";
import { useTheme } from "../../context/ThemeContext";
import type { AppItem } from "../../components/base/AppsSidebar";
import { useAccess } from "../../hooks/useAccess";
import { useMenuEntitlement } from "../../hooks/useEntitlement";
import { DOCS_NS } from "../../features/shipmentDocuments/meta";

import { MENU, DOCUMENTACION_BASE, DOCUMENTACION_APP_ID } from "./config";
import { useCompanyDashboard } from "./queries";

// Cascarón de la app «Documentación» (CC-CONSOLE #1632): submenú plano derivado
// de MENU (filtrado por derechos del preset y por banderas), encabezado, apps
// sidebar y la sección activa por <Outlet/>. Mismo patrón que MantenimientoLayout.
export default function DocumentacionLayout() {
  const { t } = useTranslation("common");
  const { t: td } = useTranslation(DOCS_NS);
  const { canAccessSectionOfApp } = useAccess();
  const menuAllowed = useMenuEntitlement(DOCUMENTACION_APP_ID);
  const { themeMode, setTheme } = useTheme();
  const filteredCategories = useFilteredCategories();
  const user = useUserStore((state) => state.user);
  const hasRedirected = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dashboard = useCompanyDashboard(30);

  const { isExpiringSoon, timeRemainingFormatted, isExpired, isAuthenticated, isInitialized } = useSessionCheck({ warningThreshold: 60 * 60 * 1000, autoRefresh: true });

  const [isAppsSidebarOpen, setIsAppsSidebarOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const openAppsSidebar = () => setIsAppsSidebarOpen(true);
  const closeAppsSidebar = () => setIsAppsSidebarOpen(false);

  const handleAppSelect = (app: AppItem) => {
    if (app.isComingSoon) {
      Swal.fire({ title: t("appsSidebar.comingSoon"), text: t("appsSidebar.comingSoonText", { name: app.name }), icon: "info", confirmButtonText: t("appsSidebar.understood"), confirmButtonColor: brand.primary });
      return;
    }
    if (app.isLocked) {
      Swal.fire({ title: t("appsSidebar.premiumFeature"), text: t("appsSidebar.premiumRequired", { name: app.name }), icon: "warning", confirmButtonText: t("appsSidebar.understood"), confirmButtonColor: brand.primary });
      return;
    }
    if (app.route) {
      closeAppsSidebar();
      navigate(app.route);
    } else if (app.externalUrl) {
      window.open(app.externalUrl, "_blank");
    }
  };

  const counts: Record<string, number | undefined> = {
    expedientes: dashboard.data?.subjects_total,
    vencimientos: dashboard.data ? dashboard.data.expiring + dashboard.data.expired : undefined,
  };

  const menuItems = MENU.filter((item) => canAccessSectionOfApp(DOCUMENTACION_APP_ID, item.id) && menuAllowed(item.id)).map((item) => ({
    id: item.id,
    label: td(item.labelKey),
    icon: item.icon,
    path: `${DOCUMENTACION_BASE}/${item.id}`,
    ...(counts[item.id] !== undefined ? { count: counts[item.id] } : {}),
  }));

  useEffect(() => {
    if (isInitialized && (isExpired || !isAuthenticated) && !hasRedirected.current) {
      hasRedirected.current = true;
      setLoggingOut(false);
      navigate("/");
    }
  }, [isExpired, isAuthenticated, isInitialized, navigate]);

  useEffect(() => {
    if (!user && location.pathname !== "/splash") {
      localStorage.setItem("lastRoute", location.pathname);
      navigate("/splash");
    }
  }, [user, location.pathname, navigate]);

  return (
    <div className="h-full bg-app">
      {isExpiringSoon && (
        <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm flex items-center justify-center gap-2">
          <i className="ri-alarm-warning-line"></i>
          <span>{t("sessionBanner.expiring", { time: timeRemainingFormatted })}</span>
        </div>
      )}

      <Header title={td("appName")} subtitle={td("appSubtitle")} onOpenAppsSidebar={openAppsSidebar} isAppsSidebarOpen={isAppsSidebarOpen} />

      <div className="flex h-full">
        <AppSidebarMenu
          menuItems={menuItems}
          isMobileMenuOpen={false}
          onCloseMobileMenu={() => {}}
          onNavigate={(path) => navigate(path)}
          isActiveRoute={(itemId) => location.pathname === `${DOCUMENTACION_BASE}/${itemId}`}
          footerStats={{
            label: td("footer.label"),
            items: [
              { label: td("footer.subjects"), value: dashboard.data?.subjects_total ?? "—" },
              { label: td("footer.documents"), value: dashboard.data?.documents_total ?? "—" },
            ],
          }}
        />

        <div className="flex-1 overflow-hidden flex justify-center w-full">
          <Outlet />
        </div>
      </div>

      <AppsSidebar
        isOpen={isAppsSidebarOpen}
        onClose={closeAppsSidebar}
        categories={filteredCategories}
        selectedAppId={DOCUMENTACION_APP_ID}
        onAppSelect={handleAppSelect}
        onStoreClick={() => navigate("/store")}
        currentTheme={themeMode}
        onThemeChange={setTheme}
        userInfo={user ? { first_name: user.first_name, last_name: user.last_name, email: user.email, username: user.username } : undefined}
        onProfileClick={() => {
          closeAppsSidebar();
          navigate("/profile");
        }}
        onLogout={async () => {
          setIsLoggingOut(true);
          await logoutUser();
          setIsLoggingOut(false);
        }}
        isLoggingOut={isLoggingOut}
      />
    </div>
  );
}
